import { useState } from "react";
import { supabase } from "@/lib/supabase";

interface Product {
  id: string;
  name: string;
  product_code: string;
  brand: string;
  purchase_price: number;
  sale_price: number;
}

interface Props {
  products: Product[];
  dealerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BulkPriceUpdateModal({ products, dealerId, onClose, onSuccess }: Props) {
  const [target, setTarget] = useState<"sale" | "purchase" | "both">("sale");
  const [mode, setMode] = useState<"percentage" | "fixed">("percentage");
  const [operation, setOperation] = useState<"increase" | "decrease">("increase");
  const [value, setValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.product_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(filtered.map((p) => p.id)));
      setSelectAll(true);
    }
  };

  const toggleProduct = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      setSelectAll(false);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const previewPrice = (price: number): number => {
    const num = parseFloat(value) || 0;
    if (mode === "percentage") {
      if (operation === "increase") return price * (1 + num / 100);
      return price * (1 - num / 100);
    }
    if (operation === "increase") return price + num;
    return price - num;
  };

  const getPreviewText = (price: number): string => {
    const newPrice = previewPrice(price);
    if (newPrice < 0) return "$0.00";
    return `$${newPrice.toFixed(2)}`;
  };

  const applyUpdate = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError("En az bir ürün seçmelisiniz.");
      return;
    }
    const num = parseFloat(value);
    if (!num || num <= 0) {
      setError("Geçerli bir değer girin.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // First fetch current prices for selected products
      const { data: currentProducts, error: fetchErr } = await supabase
        .from("products")
        .select("id, purchase_price, sale_price")
        .in("id", ids)
        .eq("dealer_id", dealerId);

      if (fetchErr) throw fetchErr;

      let updatedCount = 0;
      for (const p of currentProducts || []) {
        const updates: Record<string, number> = {};
        const salePrice = Number(p.sale_price) || 0;
        const purchasePrice = Number(p.purchase_price) || 0;

        if (target === "sale" || target === "both") {
          updates.sale_price = Math.max(0, previewPrice(salePrice));
        }
        if (target === "purchase" || target === "both") {
          updates.purchase_price = Math.max(0, previewPrice(purchasePrice));
        }

        const { error: updateErr } = await supabase
          .from("products")
          .update(updates)
          .eq("id", p.id);

        if (!updateErr) updatedCount++;
      }

      if (updatedCount > 0) {
        onSuccess();
        onClose();
      } else {
        setError("Hiçbir ürün güncellenemedi.");
      }
    } catch (err: any) {
      setError(err.message || "Güncelleme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 shrink-0">
          <h4 className="text-base font-semibold text-foreground-950">Toplu Fiyat Güncelleme</h4>
          <button onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
        </div>

        {error && (
          <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2 shrink-0">
            <i className="ri-error-warning-line"></i>
            {error}
            <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap">Kapat</button>
          </div>
        )}

        {/* Settings */}
        <div className="bg-background-100 rounded-xl p-4 space-y-3 shrink-0 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">Hedef:</span>
              <div className="flex items-center gap-1 bg-background-50 rounded-full px-1 py-1 border border-background-200/70">
                {(["sale", "purchase", "both"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTarget(t)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      target === t ? "bg-primary-500 text-background-50" : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    {t === "sale" ? "Satış" : t === "purchase" ? "Alış" : "İkisi de"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">Tür:</span>
              <div className="flex items-center gap-1 bg-background-50 rounded-full px-1 py-1 border border-background-200/70">
                {(["percentage", "fixed"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      mode === m ? "bg-primary-500 text-background-50" : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    {m === "percentage" ? "Yüzde (%)" : "Sabit Tutar ($)"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground-500">İşlem:</span>
            <div className="flex items-center gap-1 bg-background-50 rounded-full px-1 py-1 border border-background-200/70">
              <button
                onClick={() => setOperation("increase")}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  operation === "increase" ? "bg-primary-500 text-background-50" : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                Artır
              </button>
              <button
                onClick={() => setOperation("decrease")}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  operation === "decrease" ? "bg-accent-500 text-background-50" : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                Azalt
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">
                {mode === "percentage" ? "Yüzde:" : "Tutar:"}
              </span>
              <div className="relative">
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={mode === "percentage" ? "10" : "5.00"}
                  className="w-24 px-3 py-1.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  min="0"
                  step={mode === "percentage" ? "1" : "0.01"}
                />
                {mode === "percentage" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400">%</span>}
              </div>
            </div>

            {value && selectedIds.size > 0 && (
              <p className="text-xs text-foreground-400">
                {selectedIds.size} ürün {operation === "increase" ? "artırılacak" : "azaltılacak"}
              </p>
            )}
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
              />
              <span className="text-sm font-medium text-foreground-700">
                {selectAll ? "Tümünü Kaldır" : "Tümünü Seç"}
              </span>
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ürün ara..."
                className="pl-8 pr-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 w-48"
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1 border border-background-200/70 rounded-lg">
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-foreground-400 text-sm">Ürün bulunamadı</div>
            ) : (
              filtered.map((p) => {
                const isSelected = selectedIds.has(p.id);
                const currentPrice = target === "purchase" ? p.purchase_price : p.sale_price;
                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-background-100 last:border-0 cursor-pointer transition-colors ${
                      isSelected ? "bg-primary-50/70" : "hover:bg-background-100/50"
                    }`}
                    onClick={() => toggleProduct(p.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{p.name}</p>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        {p.product_code && <span>{p.product_code}</span>}
                        {p.brand && <span>{p.brand}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium text-foreground-700">${Number(currentPrice).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                      {value && isSelected && target !== "both" && (
                        <p className={`text-xs font-medium ${previewPrice(Number(currentPrice)) > Number(currentPrice) ? "text-primary-600" : "text-accent-600"}`}>
                          → {getPreviewText(Number(currentPrice))}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 mt-4 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            onClick={applyUpdate}
            disabled={saving || selectedIds.size === 0}
            className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Uygulanıyor...</span>
            ) : (
              <span className="flex items-center justify-center gap-2"><i className="ri-price-tag-3-line"></i> {selectedIds.size} Ürüne Uygula</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}